import type { CatalogProduct } from '../../types'
import type { NumericFacet, NumericFilterState, OptionFacet, OptionFilterState } from './types'
import {
  computeProductPrice,
  extractFieldValues,
  extractProductBrands,
  sanitizeOptionValue,
  toLooseNumber,
} from './productFilters.shared'

export const countActiveSearchFilters = ({
  selectedBrands,
  selectedTypes,
  priceRange,
  numericFilters,
  optionFilters,
}: {
  selectedBrands: string[]
  selectedTypes: string[]
  priceRange: { min: string; max: string }
  numericFilters: NumericFilterState
  optionFilters: OptionFilterState
}): number => {
  const brandFilters = selectedBrands.length
  const typeFilters = selectedTypes.length
  const priceFilters = (priceRange.min.trim() ? 1 : 0) + (priceRange.max.trim() ? 1 : 0)
  const numericCount = Object.values(numericFilters).reduce(
    (acc, range) => acc + (range?.min?.trim() || range?.max?.trim() ? 1 : 0),
    0,
  )
  const optionCount = Object.values(optionFilters).reduce(
    (acc, values) => acc + (Array.isArray(values) && values.length ? 1 : 0),
    0,
  )
  return brandFilters + typeFilters + priceFilters + numericCount + optionCount
}

export const filterSearchProducts = ({
  products,
  selectedBrands,
  selectedTypes,
  priceRange,
  numericFacets,
  numericFilters,
  optionFacets,
  optionFilters,
}: {
  products: CatalogProduct[]
  selectedBrands: string[]
  selectedTypes: string[]
  priceRange: { min: string; max: string }
  numericFacets: NumericFacet[]
  numericFilters: NumericFilterState
  optionFacets: OptionFacet[]
  optionFilters: OptionFilterState
}): CatalogProduct[] => {
  const minPrice = toLooseNumber(priceRange.min)
  const maxPrice = toLooseNumber(priceRange.max)

  return products.filter((product) => {
    if (selectedBrands.length) {
      const productBrands = new Set(extractProductBrands(product).map((value) => value.toLowerCase()))
      if (!productBrands.size) return false
      const matchesBrand = selectedBrands.some((value) => productBrands.has(value.toLowerCase()))
      if (!matchesBrand) return false
    }

    if (selectedTypes.length) {
      const productType = product.productType?.trim() ?? ''
      if (!productType || !selectedTypes.includes(productType)) return false
    }

    const price = computeProductPrice(product)
    if (minPrice !== null && (price === null || price < minPrice)) return false
    if (maxPrice !== null && (price === null || price > maxPrice)) return false

    for (const facet of numericFacets) {
      const range = numericFilters[facet.id]
      const min = toLooseNumber(range?.min)
      const max = toLooseNumber(range?.max)
      if (min === null && max === null) continue

      const numericValues = extractFieldValues(product, facet.source, facet.key)
        .map((value) => toLooseNumber(value))
        .filter((value): value is number => value !== null)

      if (!numericValues.length) return false
      const matches = numericValues.some((value) => {
        if (min !== null && value < min) return false
        if (max !== null && value > max) return false
        return true
      })
      if (!matches) return false
    }

    for (const facet of optionFacets) {
      const selected = optionFilters[facet.id] ?? []
      if (!selected.length) continue

      const normalizedValues = new Set(
        extractFieldValues(product, facet.source, facet.key)
          .map((value) => sanitizeOptionValue(value))
          .filter((value): value is string => Boolean(value))
          .map((value) => value.toLowerCase()),
      )

      if (!normalizedValues.size) return false
      const matches = selected.some((value) => normalizedValues.has(value.toLowerCase()))
      if (!matches) return false
    }

    return true
  })
}
